import { PlayerRef } from '@remotion/player';

import React, { useEffect, useMemo, useRef } from 'react';
import {
  MAX_AUTOSCROLL_SPEED,
  TIMELINE_HEADER_HEIGHT,
  scrollbarStyle,
} from '../constants';
import { DraggingTimelineItems } from '../drag-overlay';
import { DropHandler } from '../drop-handler';
import {
  FEATURE_ARROW_KEY_MOVE,
  FEATURE_BACKSPACE_TO_DELETE,
  FEATURE_CLIP_TRIM_SHORTCUTS,
  FEATURE_DUPLICATE_LAYERS,
  FEATURE_FOLLOW_PLAYHEAD_WHILE_PLAYING,
  FEATURE_MAX_TRIM_INDICATORS,
  FEATURE_SAVE_BUTTON,
  FEATURE_SELECT_ALL_SHORTCUT,
  FEATURE_SKIMMING,
  FEATURE_SNAPPING_SHORTCUT,
  FEATURE_TIMELINE_SNAPPING,
} from '../flags';
import { ArrowKeyMove } from '../keyboard-shortcuts/arrow-key-move';
import { BackspaceToDelete } from '../keyboard-shortcuts/backspace-to-delete';
import { ClipTrimShortcuts } from '../keyboard-shortcuts/clip-trim-shortcuts';
import { CopyPasteLayers } from '../keyboard-shortcuts/copy-paste-layers';
import { DuplicateLayers } from '../keyboard-shortcuts/duplicate-layers';
import { SaveShortcut } from '../keyboard-shortcuts/save-shortcut';
import { SelectAllShortcut } from '../keyboard-shortcuts/select-all-shortcut';
import { SnappingShortcut } from '../keyboard-shortcuts/snapping-shortcut';
import { SpaceToPlayPause } from '../keyboard-shortcuts/space-to-play-pause';
import { TimelineZoomWheel } from '../keyboard-shortcuts/timeline-zoom-wheel';
import { UndoRedo } from '../keyboard-shortcuts/undo-redo';
import { MarqueeSelection } from '../marquee-selection';
import { getVisibleFrames } from '../utils/get-visible-frames';
import { useTimelineMarqueeSelectionAndSeek } from '../utils/marquee-selection/use-timeline-marquee-selection-and-seek';
import {
  timelineRightSide,
  timelineScrollContainerRef,
} from '../utils/restore-scroll-after-zoom';
import { Z_INDEX_SIDE_PANEL } from '../z-indices';
import {
  useActiveTimelineSnap,
  useAllItems,
  useDimensions,
  useFps,
  useTimelineCollapsed,
  useTimelineContext,
  useTimelineHeight,
  useTracks,
} from '../utils/use-context';
import { useTimelineContainerAutoScroll } from '../utils/use-timeline-container-auto-scroll';
import { useDragPreview } from './drag-preview-provider';
import { Playhead } from './playhead';
import { Skimmer } from './skimmer';
import { useTimelineSkimming } from '../utils/use-timeline-skimming';
import { TimelineSnapIndicators } from './snap-indicator/timeline-snap-indicators';
import { TimelineTicks } from './ticks/ticks';
import { TICKS_HEIGHT } from './ticks/constants';
import { TimelineBackground } from './timeline-background/timeline-background';
import { TimelineMaxTrimIndicators } from './timeline-item/timeline-max-trim-indicators/timeline-max-trim-indicators';
import { TimelineFollowPlayheadWhilePlaying } from './timeline-scroll-while-playing';
import { TimelineScrollableContainer } from './timeline-scrollable-container';
import {
  SIDE_PANEL_WIDTH,
  SidePanel,
} from './timeline-side-panel/timeline-side-panel';
import { TimelineTracks } from './timeline-tracks';
import { TimelineAudioLevelMeter } from './timeline-audio-level-meter';
import { TimelineUnderlayPreviews } from './timeline-underlay-previews';
import { AssetDropPreview } from './asset-drop-preview';
import { useAssetDropPreview } from './asset-drop-preview-provider';
import { TransitionDropPreview } from './transition-drop-preview';
import { TransitionDragOverlay } from './transition-drag-overlay';
import { AnimationDropPreview } from './animation-drop-preview';
import { AnimationDragOverlay } from './animation-drag-overlay';
import { TimelineTransitionMarkers } from './timeline-transition-markers';
import { applyNewPositionsToState } from './utils/drag/apply-new-positions-to-state';
import { getTracksHeight } from './utils/drag/calculate-track-heights';
import { remainOriginalTrackHeights } from './utils/drag/remain-original-track-heights';
import { useTimelineSize } from './utils/use-timeline-size';
import { TRACK_DIVIDER_HEIGHT } from '../state/items';
import { CurrentTimeDisplay } from '../playback-controls/time-display';
import { ProjectDurationDisplay } from '../playback-controls/project-duration-display';
import { IncomingTimelineProjection } from '@waka/compat/IncomingTimelineProjection';
import { isIncomingPreviewCurrent } from '@waka/compat/incoming-preview';
import { useCurrentStateAsRef } from '../utils/use-context';

export const Timeline = ({
  playerRef,
}: {
  playerRef: React.RefObject<PlayerRef | null>;
}) => {
  const { durationInFrames } = useTimelineContext();
  const { tracks: tracksInState } = useTracks();
  const { compositionHeight, compositionWidth } = useDimensions();
  const { fps } = useFps();
  const { items: itemsInState } = useAllItems();
  const { activeSnapPoint } = useActiveTimelineSnap();
  const currentState = useCurrentStateAsRef();
  const { previewState: incomingPreview } = useAssetDropPreview();
  const assetDropPreviewState =
    incomingPreview &&
    !isIncomingPreviewCurrent(currentState.current, incomingPreview)
      ? null
      : incomingPreview;
  const incomingDocument = assetDropPreviewState?.projectedDocument ?? null;
  const skimmingContainerRef = useRef<HTMLDivElement | null>(null);

  const visibleFrames = getVisibleFrames({
    fps,
    totalDurationInFrames: durationInFrames,
  });

  const previewState = useDragPreview();

  const timelineHeight = useTimelineHeight();
  const isTimelineCollapsed = useTimelineCollapsed();

  // collapse 토글 시에만 transition 적용 (드래그 리사이즈 시에는 transition 없이 즉시 반영)
  const prevCollapsedRef = useRef(isTimelineCollapsed);
  const shouldTransition = prevCollapsedRef.current !== isTimelineCollapsed;
  useEffect(() => {
    prevCollapsedRef.current = isTimelineCollapsed;
  }, [isTimelineCollapsed]);

  const effectiveHeight = isTimelineCollapsed
    ? TIMELINE_HEADER_HEIGHT
    : timelineHeight + TIMELINE_HEADER_HEIGHT;

  const style = useMemo<React.CSSProperties>(
    () => ({
      height: effectiveHeight,
      ...(shouldTransition && { transition: 'height 300ms ease-in-out' }),
    }),
    [effectiveHeight, shouldTransition],
  );

  const containerStyles = useMemo<React.CSSProperties>(
    () => ({
      ...scrollbarStyle,
      height: effectiveHeight,
      ...(shouldTransition && { transition: 'height 300ms ease-in-out' }),
      overflowY: isTimelineCollapsed ? 'hidden' : undefined,
    }),
    [effectiveHeight, isTimelineCollapsed, shouldTransition],
  );

  // 사용자 drag로 인해 두 track 사이에 새 track이 생성되는 경우,
  // 깜빡임이 발생할 수 있으므로 새 track이 아닌 line으로만 preview합니다.
  const previewsTrackInsertionInbetween = useMemo(() => {
    if (!previewState) {
      return false;
    }

    return previewState.trackInsertions?.type === 'between';
  }, [previewState]);

  const { tracks, items } = useMemo(() => {
    if (incomingDocument)
      return { tracks: incomingDocument.tracks, items: incomingDocument.items };
    if (!previewState) {
      return { tracks: tracksInState, items: itemsInState };
    }

    if (previewsTrackInsertionInbetween) {
      return {
        tracks: tracksInState,
        items: itemsInState,
      };
    }

    return applyNewPositionsToState({
      prevTracks: tracksInState,
      dragPreview: previewState,
      prevItems: itemsInState,
      shouldRemoveEmptyTracks: false,
    });
  }, [
    tracksInState,
    previewState,
    itemsInState,
    previewsTrackInsertionInbetween,
    incomingDocument,
  ]);

  const tracksAndLayout = useMemo(() => {
    const baseLayout = remainOriginalTrackHeights({
      originalTracks: tracksInState,
      originalItems: itemsInState,
      newTracks: tracks,
      newItems: items,
    });

    // create-between 상태일 때, 해당 인덱스 이후의 트랙들을 아래로 밀기
    if (
      assetDropPreviewState?.trackTargetType === 'create-between' &&
      assetDropPreviewState.insertAtIndex !== undefined
    ) {
      const insertAtIndex = assetDropPreviewState.insertAtIndex;
      const insertedTrackHeight = baseLayout[insertAtIndex]?.height ?? 48;
      const offset = insertedTrackHeight + TRACK_DIVIDER_HEIGHT;

      return baseLayout.map((trackLayout, index) => {
        if (index >= insertAtIndex) {
          return {
            ...trackLayout,
            top: trackLayout.top + offset,
          };
        }
        return trackLayout;
      });
    }

    return baseLayout;
  }, [tracks, items, tracksInState, itemsInState, assetDropPreviewState]);

  const tracksHeight = useMemo(() => {
    const append =
      assetDropPreviewState?.trackTargetType === 'create-between' &&
      assetDropPreviewState.insertAtIndex === tracksAndLayout.length;
    return (
      getTracksHeight({ tracks: tracksAndLayout }) +
      (append ? 48 + TRACK_DIVIDER_HEIGHT : 0)
    );
  }, [tracksAndLayout, assetDropPreviewState]);

  const styles = useMemo(
    () => ({
      minHeight: timelineHeight + TIMELINE_HEADER_HEIGHT,
      height: tracksHeight + TIMELINE_HEADER_HEIGHT,
    }),
    [tracksHeight, timelineHeight],
  );

  const { timelineWidth } = useTimelineSize();

  const timelineTimeOverlayStyle = useMemo<React.CSSProperties>(
    () => ({
      height: TIMELINE_HEADER_HEIGHT,
      width: SIDE_PANEL_WIDTH,
      zIndex: Z_INDEX_SIDE_PANEL,
    }),
    [],
  );

  const {
    onPointerDown: onPointerDownEmptySpace,
    rect,
    isDragging: isDraggingMarqueeSelection,
  } = useTimelineMarqueeSelectionAndSeek({
    playerRef,
    timelineWidth,
    timelineScrollableHeight: tracksHeight + TIMELINE_HEADER_HEIGHT,
    visibleFrames,
  });

  const isDraggingItems = previewState !== null;

  const {
    skimFrame,
    originalFrame,
    isSkimming,
    hoveredItemId,
    onMouseMove: onSkimmingMouseMove,
    onMouseLeave: onSkimmingMouseLeave,
    onPointerDownCapture: onSkimmingPointerDownCapture,
    onClick: onSkimmingClick,
  } = useTimelineSkimming({
    containerRef: skimmingContainerRef,
    scrollContainerRef: timelineScrollContainerRef,
    playerRef,
    totalDurationInFrames: visibleFrames,
    timelineWidth,
    tracks: tracksAndLayout,
    items,
    isDragging: isDraggingMarqueeSelection || isDraggingItems,
  });

  useTimelineContainerAutoScroll({
    isDragging: isDraggingMarqueeSelection,
    edgeThreshold: 10,
    maxScrollSpeed: MAX_AUTOSCROLL_SPEED,
    xAxis: true,
    yAxis: true,
  });

  const inbetweenTrackIndicators = useMemo(() => {
    if (!previewState) {
      return null;
    }

    if (previewState.trackInsertions?.type !== 'between') {
      return null;
    }

    return previewState.trackInsertions.trackIndex;
  }, [previewState]);

  const snapPoint = assetDropPreviewState
    ? (assetDropPreviewState.snapPoint ?? null)
    : (previewState?.snapPoint ?? activeSnapPoint);

  return (
    <>
      <div
        className={'relative h-full w-full bg-editor-starter-bg select-none'}
        style={style}
      >
        <DropHandler
          playerRef={playerRef}
          compositionHeight={compositionHeight}
          compositionWidth={compositionWidth}
          context="timeline"
        >
          <div className="relative flex h-full min-h-0 w-full flex-row overflow-hidden bg-sys-bg-surface">
            <div
              className="relative flex min-h-0 min-w-0 flex-1 flex-row overflow-x-scroll overflow-y-scroll"
              style={containerStyles}
              ref={timelineScrollContainerRef}
              onPointerDown={onPointerDownEmptySpace}
            >
              {timelineWidth !== null ? (
                <>
                  <SidePanel
                    tracks={tracksAndLayout}
                    inbetweenTrackDropTrackIndex={inbetweenTrackIndicators}
                  />
                  <div
                    style={styles}
                    ref={(el) => {
                      timelineRightSide.current = el;
                      skimmingContainerRef.current = el;
                    }}
                    className="relative flex h-full min-w-0 flex-1 flex-col bg-sys-bg-surface"
                    onMouseMove={onSkimmingMouseMove}
                    onMouseLeave={onSkimmingMouseLeave}
                    onPointerDownCapture={onSkimmingPointerDownCapture}
                    onClick={onSkimmingClick}
                  >
                    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
                      <div
                        className="sticky top-0 w-full shrink-0"
                        style={{ zIndex: Z_INDEX_SIDE_PANEL }}
                      >
                        <TimelineTicks
                          visibleFrames={visibleFrames}
                          timelineWidth={timelineWidth}
                        />
                      </div>
                      <TimelineScrollableContainer
                        timelineWidth={timelineWidth}
                      >
                        <TimelineBackground
                          tracks={tracksAndLayout}
                          timelineWidth={timelineWidth}
                          inbetweenTrackDropTrackIndex={
                            inbetweenTrackIndicators
                          }
                        />
                        <IncomingTimelineProjection document={incomingDocument}>
                          <TimelineTracks
                            tracks={tracksAndLayout}
                            visibleFrames={visibleFrames}
                          />
                          <TimelineTransitionMarkers
                            timelineWidth={timelineWidth}
                            tracksLayout={tracksAndLayout}
                          />
                        </IncomingTimelineProjection>
                        {previewState && !previewsTrackInsertionInbetween ? (
                          <TimelineUnderlayPreviews
                            timelineWidth={timelineWidth}
                            simulatedTracks={tracksAndLayout}
                            previewState={previewState}
                          />
                        ) : null}
                        <AssetDropPreview timelineWidth={timelineWidth} />
                        <TransitionDropPreview timelineWidth={timelineWidth} />
                        <TransitionDragOverlay />
                        <AnimationDropPreview timelineWidth={timelineWidth} />
                        <AnimationDragOverlay />
                        {FEATURE_MAX_TRIM_INDICATORS ? (
                          <TimelineMaxTrimIndicators
                            timelineWidth={timelineWidth}
                          />
                        ) : null}
                        {FEATURE_TIMELINE_SNAPPING && snapPoint ? (
                          <TimelineSnapIndicators
                            timelineWidth={timelineWidth}
                            activeSnapPoint={snapPoint}
                          />
                        ) : null}
                      </TimelineScrollableContainer>
                      <Playhead
                        visibleFrames={visibleFrames}
                        playerRef={playerRef}
                        height={tracksHeight}
                        timelineWidth={timelineWidth}
                        durationInFrames={durationInFrames}
                        overrideFrame={isSkimming ? originalFrame : undefined}
                      />
                      {FEATURE_SKIMMING && isSkimming && skimFrame !== null ? (
                        <Skimmer
                          skimFrame={skimFrame}
                          visibleFrames={visibleFrames}
                          height={tracksHeight}
                          timelineWidth={timelineWidth}
                        />
                      ) : null}
                      {rect ? <MarqueeSelection selection={rect} /> : null}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
            {timelineWidth !== null ? (
              <div
                style={timelineTimeOverlayStyle}
                className="pointer-events-none absolute top-0 left-0 border-r-2 border-r-sys-style-neutral-default bg-sys-bg-surface"
              >
                <div
                  className="flex w-full items-center pl-md"
                  style={{ height: TICKS_HEIGHT }}
                >
                  <div className="flex flex-col gap-4xs">
                    <CurrentTimeDisplay playerRef={playerRef} fps={fps} />
                    <ProjectDurationDisplay fps={fps} />
                  </div>
                </div>
              </div>
            ) : null}
            {timelineWidth !== null ? (
              <div
                className="pointer-events-none flex w-[clamp(1.8rem,6.57vw,4.2rem)] shrink-0 flex-col self-stretch border-l border-sys-style-neutral-default bg-sys-bg-surface"
                aria-hidden
              >
                <TimelineAudioLevelMeter
                  playerRef={playerRef}
                  className="min-h-0 flex-1"
                />
              </div>
            ) : null}
          </div>
        </DropHandler>
      </div>
      <SpaceToPlayPause playerRef={playerRef} />
      {FEATURE_BACKSPACE_TO_DELETE && <BackspaceToDelete />}
      {FEATURE_CLIP_TRIM_SHORTCUTS && (
        <ClipTrimShortcuts
          playerRef={playerRef}
          skimFrame={skimFrame}
          isSkimming={isSkimming}
          hoveredItemId={hoveredItemId}
        />
      )}
      {FEATURE_DUPLICATE_LAYERS && <DuplicateLayers />}
      {FEATURE_ARROW_KEY_MOVE && <ArrowKeyMove />}
      <UndoRedo />
      {FEATURE_SAVE_BUTTON && <SaveShortcut />}
      <CopyPasteLayers playerRef={playerRef} />
      {FEATURE_SELECT_ALL_SHORTCUT && (
        <SelectAllShortcut
          playerRef={playerRef}
          skimFrame={skimFrame}
          isSkimming={isSkimming}
        />
      )}
      {FEATURE_TIMELINE_SNAPPING && FEATURE_SNAPPING_SHORTCUT && (
        <SnappingShortcut />
      )}
      <DraggingTimelineItems />
      {FEATURE_FOLLOW_PLAYHEAD_WHILE_PLAYING ? (
        <TimelineFollowPlayheadWhilePlaying
          playerRef={playerRef}
          timelineWidth={timelineWidth}
          visibleFrames={visibleFrames}
        />
      ) : null}
      <TimelineZoomWheel />
    </>
  );
};
